import { Calendar, User, Tag, ArrowRight } from 'lucide-react'
import Hero from '../components/Hero'
import SEO from '../components/SEO'

export default function Blog() {
  const blogPosts = [
    {
      title: 'The Future of Theological Education in Africa',
      excerpt: 'Exploring how theological education is evolving to meet the needs of 21st-century ministry while maintaining biblical foundations...',
      author: 'Rev. Doc. Doreen Mwendesi',
      date: 'January 15, 2024',
      category: 'Theology',
      image: '/assets/images/pexels-pixabay-268362.jpg',
      tags: ['Theology', 'Education', 'Africa']
    },
    {
      title: 'Leadership Lessons from Biblical Figures',
      excerpt: 'What can modern leaders learn from the leadership styles of Moses, David, and Paul? Timeless principles for contemporary challenges...',
      author: 'Pastor John Mwangi',
      date: 'January 10, 2024',
      category: 'Leadership',
      image: '/assets/images/pexels-kampus-7551674.jpg',
      tags: ['Leadership', 'Bible', 'Ministry']
    },
    {
      title: 'Compassionate Care: The Heart of Christian Nursing',
      excerpt: 'How Christian values transform healthcare practice. Exploring the integration of faith and professional nursing excellence...',
      author: 'Dr. Sarah Chikwanha',
      date: 'January 5, 2024',
      category: 'Nursing',
      image: '/assets/images/pexels-rdne-6129194.jpg',
      tags: ['Nursing', 'Healthcare', 'Service']
    },
    {
      title: 'Sustainable Architecture for African Communities',
      excerpt: 'Designing buildings that serve communities while respecting the environment. Innovative approaches to sustainable construction in Africa...',
      author: 'Arch. Michael Banda',
      date: 'December 28, 2023',
      category: 'Architecture',
      image: '/assets/images/pexels-pixabay-372326.jpg',
      tags: ['Architecture', 'Sustainability', 'Community']
    },
    {
      title: 'Empowering Women in Ministry and Leadership',
      excerpt: 'Breaking barriers and embracing God\'s call. How women are transforming ministry and leadership across Africa...',
      author: 'Rev. Doc. Doreen Mwendesi',
      date: 'December 20, 2023',
      category: 'Leadership',
      image: '/assets/images/pexels-cottonbro-6275777.jpg',
      tags: ['Women', 'Ministry', 'Leadership']
    },
    {
      title: 'Online Learning: Expanding Access to Quality Education',
      excerpt: 'How digital platforms are making theological and leadership education accessible to students across Africa and beyond...',
      author: 'Academic Team',
      date: 'December 15, 2023',
      category: 'Education',
      image: '/assets/images/kimberly-farmer-lUaaKCUANVI-unsplash.jpg',
      tags: ['Online Learning', 'Technology', 'Access']
    }
  ]

  const categories = ['All', 'Theology', 'Leadership', 'Nursing', 'Architecture', 'Education']

  return (
    <>
      <SEO 
        title="News & Blog - Rudovia Life College"
        description="Read articles on theology, leadership, nursing, and education in Africa. Insights from faculty and thought leaders at Rudovia Life College."
        keywords="theology blog, leadership articles, nursing education, Christian education blog, theology college in Africa, leadership courses online"
      />
      <div className="pt-20">
        <Hero
          title="News & Blog"
          subtitle="Insights, inspiration, and updates from Rudovia Life College"
          image="/assets/images/jonny-swales-fIDIgxZpdSM-unsplash.jpg"
          height="h-[400px]"
        />

        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {categories.map((category) => (
                <button
                  key={category}
                  className="px-6 py-2 rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-white transition-colors font-semibold"
                >
                  {category}
                </button>
              ))}
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post, index) => (
                <article key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow group">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-secondary text-white px-3 py-1 rounded-full text-sm font-semibold">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{post.date}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <User className="w-4 h-4" />
                        <span>{post.author}</span>
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-primary mb-3 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.tags.map((tag, tagIndex) => (
                        <span 
                          key={tagIndex}
                          className="flex items-center space-x-1 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
                        >
                          <Tag className="w-3 h-3" />
                          <span>{tag}</span>
                        </span>
                      ))}
                    </div>
                    <button className="text-secondary hover:text-orange-600 font-semibold flex items-center space-x-2 transition-colors">
                      <span>Read More</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </article>
              ))}
            </div>

            <div className="text-center mt-12">
              <button className="btn-outline">
                Load More Articles
              </button>
            </div>
          </div>
        </section>

        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <h2 className="heading-2 mb-6 text-center">Subscribe to Our Newsletter</h2>
              <p className="text-xl text-gray-600 text-center mb-8">
                Get the latest articles, updates, and insights delivered to your inbox
              </p>
              <form className="flex flex-col sm:flex-row gap-4">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className="flex-1 px-6 py-4 border-2 border-gray-300 rounded-lg focus:border-secondary focus:outline-none"
                  required
                />
                <button type="submit" className="btn-primary whitespace-nowrap">
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        </section>

        <section className="section-padding bg-primary text-white">
          <div className="container-custom text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Stay Connected</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Follow us on social media for daily inspiration, updates, and community stories
            </p>
            <div className="flex justify-center space-x-4">
              <a href="https://www.facebook.com/Rudovia" target="_blank" rel="noopener noreferrer" className="btn-secondary">
                Facebook
              </a>
              <a href="https://www.linkedin.com/company/111399087/admin/dashboard/" target="_blank" rel="noopener noreferrer" className="btn-secondary">
                LinkedIn
              </a>
            </div>
            <p className="mt-8 text-lg font-semibold text-secondary">
              #RudoviaCollege #DoreenSpeaks
            </p>
          </div>
        </section>
      </div>
    </>
  )
}
